import { useState, useEffect } from 'react';
import { Modal, Form, DatePicker, Input, Button, Row, Col, Spin, Popover, message, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import GoogleAutoComplete from 'react-google-autocomplete';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { updateOrderDatesRental, checkZipCode, getOrderDetails, getUnavaliableDays, getBlockedDays, getDeliverWindows, getPickupWindows } from '../../../../../data/API';
import fetchBubbleContent from '../../../../../utils';

const DateChangeModal = ({ open, onClose, selectedOrder, onOrderUpdate }) => {
  const [form] = Form.useForm();
  const [, forceUpdate] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [isDeliveryDatePassed, setIsDeliveryDatePassed] = useState(false);
  const [isPickupDatePassed, setIsPickupDatePassed] = useState(false);
  const [isDeliveryAddressChangeBlocked, setIsDeliveryAddressChangeBlocked] = useState(false);
  const [isPickupAddressChangeBlocked, setIsPickupAddressChangeBlocked] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [zipCodeError, setZipCodeError] = useState(null);
  const [pickupZipCodeError, setPickupZipCodeError] = useState(null);
  const [bubbleContents, setBubbleContents] = useState({});
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [blockedDays, setBlockedDays] = useState([]);
  
  // New state for tracking valid Google Maps selections
  const [isDeliveryAddressValid, setIsDeliveryAddressValid] = useState(true);
  const [isPickupAddressValid, setIsPickupAddressValid] = useState(true);
  const [deliveryAddressManuallyChanged, setDeliveryAddressManuallyChanged] = useState(false);
  const [pickupAddressManuallyChanged, setPickupAddressManuallyChanged] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Time slot states
  const [deliveryTimeSlots, setDeliveryTimeSlots] = useState([]);
  const [pickupTimeSlots, setPickupTimeSlots] = useState([]);
  const [isTimeSlotChangeBlocked, setIsTimeSlotChangeBlocked] = useState(false);

  const [originalRentalDuration, setOriginalRentalDuration] = useState(null);
  const [originalPickupDate, setOriginalPickupDate] = useState(null);
  const [rentalDurationError, setRentalDurationError] = useState(null);

  const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
  const obj = { title: "Address" };

  useEffect(() => {
    if (open && selectedOrder) {
      fetchOrderDetails();
      fetchBlockedDays();
      fetchUnavailableDates();
      fetchTimeSlots();
    }
  }, [open, selectedOrder]);

  const fetchBlockedDays = async () => {
    try {
      const response = await getBlockedDays();
      const blockedDaysArray = response?.data?.results?.map((obj) => obj.day) || [];
      setBlockedDays(blockedDaysArray);
    } catch (error) {
      console.error('Error fetching blocked days:', error);
    }
  };

  const fetchUnavailableDates = async () => {
    try {
      let allDates = [];
      let currentPage = 1;
      let hasNextPage = true;

      while (hasNextPage) {
        const response = await getUnavaliableDays(currentPage);

        if (response.data.results) {
          allDates = [...allDates, ...response.data.results];
        }

        if (response.data.next) {
          currentPage++;
        } else {
          hasNextPage = false;
        }
      }

      setUnavailableDates(allDates);
    } catch (error) {
      console.error('Error fetching unavailable dates:', error);
    }
  };

  // Helper function to convert time string to minutes for sorting
  const timeToMinutes = (timeStr) => {
    if (!timeStr || timeStr.toLowerCase().includes('anytime')) {
      return 9999; // Put "Anytime" at the end
    }

    const match = timeStr.match(/(\d+):(\d+)\s*(am|pm)/i);
    if (!match) return 9999;

    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3].toLowerCase();

    if (period === 'pm' && hours !== 12) {
      hours += 12;
    } else if (period === 'am' && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes;
  };

  const fetchTimeSlots = async () => {
    try {
      const [deliveryRes, pickupRes] = await Promise.all([
        getDeliverWindows(),
        getPickupWindows()
      ]);

      if (deliveryRes?.data?.results) {
        // Sort by display_order first, then by actual time value
        const sortedDeliverySlots = [...deliveryRes.data.results].sort((a, b) => {
          if (a.display_order !== b.display_order) {
            return a.display_order - b.display_order;
          }
          return timeToMinutes(a.start_time) - timeToMinutes(b.start_time);
        });
        setDeliveryTimeSlots(sortedDeliverySlots);
      }

      if (pickupRes?.data?.results) {
        // Sort by display_order first, then by actual time value
        const sortedPickupSlots = [...pickupRes.data.results].sort((a, b) => {
          if (a.display_order !== b.display_order) {
            return a.display_order - b.display_order;
          }
          return timeToMinutes(a.start_time) - timeToMinutes(b.start_time);
        });
        setPickupTimeSlots(sortedPickupSlots);
      }
    } catch (error) {
      console.error('Error fetching time slots:', error);
    }
  };

  const calculateRentalDuration = (deliveryDate, pickupDate) => {
    if (!deliveryDate || !pickupDate) return null;
    return pickupDate.diff(deliveryDate, 'day');
  };

  const disabledDeliveryDate = (current) => {
    if (!current) return false;

    const today = dayjs();

    if (current.isBefore(today, 'day')) return true;

    const hoursDifference = current.diff(today, 'hours');
    if (hoursDifference < 48) {
      return true;
    }

    const currentDayName = current.format('dddd'); 
    if (blockedDays.includes(currentDayName)) {
      return true;
    }

    const currentDateString = current.format('YYYY-MM-DD');
    if (unavailableDates.some(dateObj => dayjs(dateObj.date).format('YYYY-MM-DD') === currentDateString)) {
      return true;
    }

    return false;
  };

  const disabledPickupDate = (current) => {
    if (!current) return false;

    const today = dayjs();
    const deliveryDate = form.getFieldValue('delivery_date');

    if (current.isBefore(today, 'day')) return true;

    const hoursDifference = current.diff(today, 'hours');
    if (hoursDifference < 48) {
      return true;
    }

    const currentDayName = current.format('dddd'); 
    if (blockedDays.includes(currentDayName)) {
      return true;
    }

    const currentDateString = current.format('YYYY-MM-DD');
    if (unavailableDates.some(dateObj => dayjs(dateObj.date).format('YYYY-MM-DD') === currentDateString)) {
      return true;
    }

    // Pickup cannot be before delivery date
    if (!isDeliveryDatePassed && deliveryDate && current.isBefore(deliveryDate, 'day')) {
      return true;
    }

    // Restrict pickup date to not exceed original pickup date
    if (originalPickupDate) {
      return current.isAfter(originalPickupDate, 'day');
    }
    
    return false;
  };

  const fetchOrderDetails = async () => {
    try {
      setIsLoadingOrder(true);
      const response = await getOrderDetails(selectedOrder.order_id || selectedOrder.order_number);
      
      if (response?.data?.status === 'success') {
        setOrderDetails(response.data.data);
        initializeForm(response.data.data);
      } else {
        throw new Error('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      message.error({
        content: 'Failed to load order details',
        duration: 5,
        style: { marginTop: '20px' }
      });
      // Fallback to using selectedOrder directly
      setOrderDetails(selectedOrder);
      initializeForm(selectedOrder);
    } finally {
      setIsLoadingOrder(false);
    }
  };

  const initializeForm = (orderData) => {
    const deliveryDate = orderData?.delivery_adress?.delivery_date ||
                        orderData?.delivery_details?.delivery_date ||
                        orderData?.delivery_date;

    const pickupDate = orderData?.pickup_adress?.pickup_date ||
                        orderData?.pickup_details?.pickup_date ||
                        orderData?.pickup_date;

    const deliveryAddr = orderData?.delivery_adress?.delivery_address ||
                        orderData?.delivery_details?.delivery_address ||
                        orderData?.delivery_address;

    const pickupAddr = orderData?.pickup_adress?.pickup_address ||
                        orderData?.pickup_details?.pickup_address ||
                        orderData?.pickup_address;

    const deliveryAptNo = orderData?.delivery_adress?.apt_number ||
                        orderData?.delivery_details?.apt_number ||
                        orderData?.delivery_apt_no;

    const pickupAptNo = orderData?.pickup_adress?.apt_number ||
                        orderData?.pickup_details?.apt_number ||
                        orderData?.pickup_apt_no;

    // Get time slot data
    const deliveryWindow1 = orderData?.delivery_adress?.delivery_window_1?.display ||
                           orderData?.delivery_adress?.selected_window_1 ||
                           '-';
    const deliveryWindow2 = orderData?.delivery_adress?.delivery_window_2?.display ||
                           orderData?.delivery_adress?.selected_window_2 ||
                           '-';
    const pickupWindow1 = orderData?.pickup_adress?.pickup_window_1?.display ||
                         orderData?.pickup_adress?.selected_window_1 ||
                         '-';
    const pickupWindow2 = orderData?.pickup_adress?.pickup_window_2?.display ||
                         orderData?.pickup_adress?.selected_window_2 ||
                         '-';

    form.resetFields();
    
    const today = dayjs();
    const currentDeliveryDate = deliveryDate ? dayjs(deliveryDate) : null;
    const currentPickupDate = pickupDate ? dayjs(pickupDate) : null;
 
    if (currentDeliveryDate && currentPickupDate) {
      const duration = calculateRentalDuration(currentDeliveryDate, currentPickupDate);
      console.log('Setting original rental duration:', duration);
      setOriginalRentalDuration(duration);
      setOriginalPickupDate(currentPickupDate);
    }
    
    const isDeliveryDatePassed = currentDeliveryDate && currentDeliveryDate.isBefore(today, 'day');
    const isPickupDatePassed = currentPickupDate && currentPickupDate.isBefore(today, 'day');
    
    const isDeliveryAddressChangeBlocked = currentDeliveryDate && currentDeliveryDate.diff(today, 'hour') < 48;
    const isPickupAddressChangeBlocked = currentPickupDate && currentPickupDate.diff(today, 'hour') < 48;

    // Time slot changes are blocked if either delivery or pickup is within 48 hours
    const isTimeSlotBlocked = isDeliveryAddressChangeBlocked || isPickupAddressChangeBlocked;
    
    form.setFieldsValue({
        order_id: orderData?.order_id || orderData?.order_number,
        delivery_date: deliveryDate ? dayjs(deliveryDate) : null,
        pickup_date: pickupDate ? dayjs(pickupDate) : null,
        delivery_addr: deliveryAddr || '',
        pickup_addr: pickupAddr || '',
        delivery_apt_number: deliveryAptNo || '',
        pickup_apt_number: pickupAptNo || '',
        delivery_window_1: deliveryWindow1,
        delivery_window_2: deliveryWindow2,
        pickup_window_1: pickupWindow1,
        pickup_window_2: pickupWindow2
    });
    
    setDeliveryAddress(deliveryAddr || '');
    setPickupAddress(pickupAddr || '');
    setIsDeliveryDatePassed(isDeliveryDatePassed);
    setIsPickupDatePassed(isPickupDatePassed);
    setIsDeliveryAddressChangeBlocked(isDeliveryAddressChangeBlocked);
    setIsPickupAddressChangeBlocked(isPickupAddressChangeBlocked);
    setIsTimeSlotChangeBlocked(isTimeSlotBlocked);
    
    // Reset validation states
    setIsDeliveryAddressValid(true);
    setIsPickupAddressValid(true);
    setDeliveryAddressManuallyChanged(false);
    setPickupAddressManuallyChanged(false);
    setShowValidationErrors(false);
    setRentalDurationError(null);
  };

  const validateRentalDuration = (deliveryDate, pickupDate) => {
    if (!deliveryDate || !pickupDate || originalRentalDuration === null) {
      setRentalDurationError(null);
      return true;
    }

    const newDuration = calculateRentalDuration(deliveryDate, pickupDate);
    
    if (newDuration > originalRentalDuration) {
      setRentalDurationError(`Cannot extend rental duration here. Use "Extend Rental" option instead.`);
      return false;
    }
    
    setRentalDurationError(null);
    return true;
  };

  const setAddress = async (place) => {
    setDeliveryAddress(place?.formatted_address);
    form.setFieldsValue({ delivery_addr: place?.formatted_address });
    setIsDeliveryAddressValid(true);
    setDeliveryAddressManuallyChanged(false);

    let postal;
    place.address_components.forEach((address_element) => {
      if (address_element.types.includes('postal_code')) postal = address_element.long_name;
    });

    try {
      const zipCodeRes = await checkZipCode({ deliveryzipcode: postal});
      if (zipCodeRes?.data?.success === true) {
        setZipCodeError(null);
      } else {
        setZipCodeError(zipCodeRes?.data?.message_2);
      }
    } catch (error) {
      console.error("Error checking zip code:", error);
    }
  };

  const setPickupAddressFunc = async (place) => {
    setPickupAddress(place?.formatted_address);
    form.setFieldsValue({ pickup_addr: place?.formatted_address });
    setIsPickupAddressValid(true);
    setPickupAddressManuallyChanged(false);

    let postal;
    place.address_components.forEach((address_element) => {
      if (address_element.types.includes('postal_code')) postal = address_element.long_name;
    });

    try {
      const zipCodeRes = await checkZipCode({ deliveryzipcode: postal});
      if (zipCodeRes?.data?.success === true) {
        setPickupZipCodeError(null);
      } else {
        setPickupZipCodeError(zipCodeRes?.data?.message_2);
      }
    } catch (error) {
      console.error("Error checking pickup zip code:", error);
    }
  };

  const handleDeliveryAddressChange = (e) => {
    const value = e.target.value;
    if (!isDeliveryAddressChangeBlocked) {
      setDeliveryAddress(value);
      form.setFieldsValue({ delivery_addr: value });
      
      // Mark as manually changed if user types
      if (value !== deliveryAddress) {
        setDeliveryAddressManuallyChanged(true);
        setIsDeliveryAddressValid(false);
      }
      
      // If user clears the field, reset validation
      if (value === '') {
        setDeliveryAddressManuallyChanged(false);
        setIsDeliveryAddressValid(true);
      }
    }
  };

  const handlePickupAddressChange = (e) => {
    const value = e.target.value;
    if (!isPickupAddressChangeBlocked) {
      setPickupAddress(value);
      form.setFieldsValue({ pickup_addr: value });
      
      // Mark as manually changed if user types
      if (value !== pickupAddress) {
        setPickupAddressManuallyChanged(true);
        setIsPickupAddressValid(false);
      }
      
      // If user clears the field, reset validation
      if (value === '') {
        setPickupAddressManuallyChanged(false);
        setIsPickupAddressValid(true);
      }
    }
  };

  const onSubmit = async () => {
    try {
        setIsSubmitting(true);
        setShowValidationErrors(true);
    
        const formValues = form.getFieldsValue();
        console.log('Form values before validation:', formValues);

        // Validate rental duration before proceeding
        const isValidDuration = validateRentalDuration(formValues.delivery_date, formValues.pickup_date);
        if (!isValidDuration) {
          message.error({
            content: rentalDurationError,
            duration: 8,
            style: { marginTop: '20px' }
          });
          return;
        }
    
        await form.validateFields(['order_id']);
        
        const orderId = orderDetails?.order_id || selectedOrder?.order_id;
        const orderIdString = String(orderId);
        const requestData = {};

        // Get original values for comparison
        const originalDeliveryDate = orderDetails?.delivery_adress?.delivery_date || 
                                    orderDetails?.delivery_details?.delivery_date || 
                                    orderDetails?.delivery_date;
        const originalPickupDate = orderDetails?.pickup_adress?.pickup_date || 
                                  orderDetails?.pickup_details?.pickup_date || 
                                  orderDetails?.pickup_date;
        const originalDeliveryAddr = orderDetails?.delivery_adress?.delivery_address || 
                                    orderDetails?.delivery_details?.delivery_address || 
                                    orderDetails?.delivery_address;
        const originalPickupAddr = orderDetails?.pickup_adress?.pickup_address || 
                                  orderDetails?.pickup_details?.pickup_address || 
                                  orderDetails?.pickup_address;
        const originalDeliveryApt = orderDetails?.delivery_adress?.apt_number || 
                                   orderDetails?.delivery_details?.apt_number || 
                                   orderDetails?.delivery_apt_no;
        const originalPickupApt = orderDetails?.pickup_adress?.apt_number || 
                                 orderDetails?.pickup_details?.apt_number || 
                                 orderDetails?.pickup_apt_no;

        // Only add delivery date if it's actually changed
        if (formValues.delivery_date) {
          const newDeliveryDate = formValues.delivery_date.format('MM/DD/YYYY');
          const originalDeliveryDateFormatted = originalDeliveryDate ? 
            dayjs(originalDeliveryDate).format('MM/DD/YYYY') : null;
          
          if (newDeliveryDate !== originalDeliveryDateFormatted) {
            requestData.delivery_date = newDeliveryDate;
          }
        }
    
        // Only add pickup date if it's actually changed
        if (formValues.pickup_date) {
          const newPickupDate = formValues.pickup_date.format('MM/DD/YYYY');
          const originalPickupDateFormatted = originalPickupDate ?
            dayjs(originalPickupDate).format('MM/DD/YYYY') : null;

          if (newPickupDate !== originalPickupDateFormatted) {
            requestData.pickup_date = newPickupDate;
          }
        }

        // Additional validation: Check if existing dates were cleared (set to empty)
        if (originalDeliveryDate && !formValues.delivery_date) {
          message.error({
            content: 'Delivery date cannot be empty. Please select a valid delivery date or keep the original date.',
            duration: 5,
            style: { marginTop: '20px' }
          });
          return;
        }

        if (originalPickupDate && !formValues.pickup_date) {
          message.error({
            content: 'Pickup date cannot be empty. Please select a valid pickup date or keep the original date.',
            duration: 5,
            style: { marginTop: '20px' }
          });
          return;
        }

        // Strict validation for delivery address
        if (formValues.delivery_addr && formValues.delivery_addr.trim() && !isDeliveryAddressChangeBlocked) {
          const newDeliveryAddr = formValues.delivery_addr.trim();
          const originalDeliveryAddrTrimmed = (originalDeliveryAddr || '').trim();
          
          if (newDeliveryAddr !== originalDeliveryAddrTrimmed) {
            // Check if address was manually typed without selecting from Google Maps
            if (deliveryAddressManuallyChanged && !isDeliveryAddressValid) {
              message.error({
                content: 'Please select a valid delivery address from the Google Maps suggestions.',
                duration: 5,
                style: { marginTop: '20px' }
              });
              return;
            }
            
            // Check if zip code is valid
            if (zipCodeError) {
              message.error({
                content: 'Please select a delivery address within our service area.',
                duration: 5,
                style: { marginTop: '20px' }
              });
              return;
            }
            
            requestData.delivery_address = newDeliveryAddr;
          }
        }

        // Strict validation for pickup address
        if (formValues.pickup_addr && formValues.pickup_addr.trim() && !isPickupAddressChangeBlocked) {
          const newPickupAddr = formValues.pickup_addr.trim();
          const originalPickupAddrTrimmed = (originalPickupAddr || '').trim();
          
          if (newPickupAddr !== originalPickupAddrTrimmed) {
            // Check if address was manually typed without selecting from Google Maps
            if (pickupAddressManuallyChanged && !isPickupAddressValid) {
              message.error({
                content: 'Please select a valid pickup address from the Google Maps suggestions.',
                duration: 5,
                style: { marginTop: '20px' }
              });
              return;
            }
            
            // Check if zip code is valid
            if (pickupZipCodeError) {
              message.error({
                content: 'Please select a pickup address within our service area.',
                duration: 5,
                style: { marginTop: '20px' }
              });
              return;
            }
            
            requestData.pickup_address = newPickupAddr;
          }
        }

        // Only add delivery apt number if it's actually changed and not blocked
        if (formValues.delivery_apt_number !== undefined && !isDeliveryAddressChangeBlocked) {
          const newDeliveryApt = (formValues.delivery_apt_number || '').toString().trim();
          const originalDeliveryAptTrimmed = (originalDeliveryApt || '').toString().trim();
          
          if (newDeliveryApt !== originalDeliveryAptTrimmed) {
            requestData.delivery_apt_no = newDeliveryApt;
          }
        }

        // Only add pickup apt number if it's actually changed and not blocked
        if (formValues.pickup_apt_number !== undefined && !isPickupAddressChangeBlocked) {
          const newPickupApt = (formValues.pickup_apt_number || '').toString().trim();
          const originalPickupAptTrimmed = (originalPickupApt || '').toString().trim();
          
          if (newPickupApt !== originalPickupAptTrimmed) {
            requestData.pickup_apt_no = newPickupApt;
          }
        }

        // Get original time slot values
        const originalDeliveryWindow1 = orderDetails?.delivery_adress?.delivery_window_1?.display ||
                                        orderDetails?.delivery_adress?.selected_window_1 || '';
        const originalDeliveryWindow2 = orderDetails?.delivery_adress?.delivery_window_2?.display ||
                                        orderDetails?.delivery_adress?.selected_window_2 || '';
        const originalPickupWindow1 = orderDetails?.pickup_adress?.pickup_window_1?.display ||
                                      orderDetails?.pickup_adress?.selected_window_1 || '';
        const originalPickupWindow2 = orderDetails?.pickup_adress?.pickup_window_2?.display ||
                                      orderDetails?.pickup_adress?.selected_window_2 || '';

        // Only add time slots if they're actually changed and not blocked
        if (!isTimeSlotChangeBlocked) {
          if (formValues.delivery_window_1 && formValues.delivery_window_1 !== originalDeliveryWindow1) {
            requestData.delivery_selected_window_1 = formValues.delivery_window_1;
          }

          if (formValues.delivery_window_2 && formValues.delivery_window_2 !== originalDeliveryWindow2) {
            requestData.delivery_selected_window_2 = formValues.delivery_window_2;
          }

          if (formValues.pickup_window_1 && formValues.pickup_window_1 !== originalPickupWindow1) {
            requestData.pickup_selected_window_1 = formValues.pickup_window_1;
          }

          if (formValues.pickup_window_2 && formValues.pickup_window_2 !== originalPickupWindow2) {
            requestData.pickup_selected_window_2 = formValues.pickup_window_2;
          }
        }

        console.log('Request data being sent:', requestData);

        // If no changes detected, show success message and close modal
        if (Object.keys(requestData).length === 0) {
          message.success({
            content: 'Your order details are already up to date.',
            duration: 3,
            style: { marginTop: '20px' }
          });
          onClose();
          return;
        }

        const response = await updateOrderDatesRental(orderIdString, requestData);
        console.log('API Response:', response);
        
        if (response?.data?.status === 'success') {
        // Close the date change modal first
        onClose();
        form.resetFields();

        message.success({
          content: response?.data?.message || 'Change request submitted successfully! Admin will review and contact you soon.',
          duration: 5,
          style: { marginTop: '20px' }
        });

        if (onOrderUpdate && typeof onOrderUpdate === 'function') {
          console.log('Triggering customer data refresh after address/date/time slot update...');
          await onOrderUpdate();
        }
        } else {
        message.error({
          content: response?.data?.message || 'Failed to submit change request. Please try again.',
          duration: 5,
          style: { marginTop: '20px' }
        });
        }
        
    } catch (error) {
        console.error('Error submitting change request:', error);
        console.error('Error details:', error.response?.data);

        if (error.errorFields && error.errorFields.length > 0) {
        console.error('Validation errors:', error.errorFields);
        message.error({
          content: 'Please fill in all required fields correctly.',
          duration: 5,
          style: { marginTop: '20px' }
        });
        } else if (error.response?.data?.message) {
        message.error({
          content: error.response.data.message,
          duration: 5,
          style: { marginTop: '20px' }
        });
        } else {
        message.error({
          content: 'Failed to submit change request. Please try again or contact support.',
          duration: 5,
          style: { marginTop: '20px' }
        });
        }
    } finally {
        setIsSubmitting(false);
    }
    };

  const handleCancel = () => {
    onClose();
    form.resetFields();
    setIsDeliveryAddressChangeBlocked(false);
    setIsPickupAddressChangeBlocked(false);
    setIsDeliveryAddressValid(true);
    setIsPickupAddressValid(true);
    setDeliveryAddressManuallyChanged(false);
    setPickupAddressManuallyChanged(false);
  };

  // const handleSuccessModalClose = () => {
  //   setShowSuccessModal(false);
  //   setSuccessData(null);
  // };

  if (!selectedOrder) return null;

  return (
    <Modal
      footer={false}
      centered
      title={null}
      closable={false}
      open={open}
      width={800}
      className="date-change-modal modal-custom"
    >
      {(isSubmitting || isLoadingOrder) && (
        <div className="date-change-loading-overlay">
          <div className="date-change-loading-content">
            <Spin size="large" />
            <div className="date-change-loading-text">
              {isLoadingOrder ? 'Loading order details...' : 'Processing your request...'}
            </div>
          </div>
        </div>
      )}

      <div className="new-modal-header modal-custom">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span></span>
          <span>Request Date & Address Change</span>
          <CloseOutlined 
            className="new-modal-close-icon cursor-pointer" 
            onClick={handleCancel}
            style={{ color: 'white', fontSize: '16px' }}
          />
        </div>
      </div>

      <div className="new-modal-content modal-custom">
        <Form form={form} layout="vertical" autoComplete="off">
          
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item 
                name="order_id" 
                label={<span style={{ fontSize: '14px', fontWeight: '500' }}>Order ID</span>}
                rules={[{ required: true, message: 'Order ID is required' }]}
              >
                <Input 
                  size="large" 
                  placeholder="Order ID" 
                  disabled={true}
                  style={{ 
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px'
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: '20px' }}>
            <Col span={12}>
              <Form.Item 
                name="delivery_date" 
                label={
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Delivery Date
                  </span>
                }
              >
                <DatePicker 
                  size="large" 
                  className="date-change-datepicker"
                  format="MM/DD/YYYY"
                  disabled={isSubmitting || isDeliveryDatePassed}
                  allowClear={!isDeliveryDatePassed}
                  showToday={true}
                  placeholder={isDeliveryDatePassed ? "Date has passed" : "Select delivery date"}
                  style={{ 
                    width: '100%',
                    borderRadius: '6px'
                  }}
                  disabledDate={disabledDeliveryDate}
                  onChange={(date) => {
                    if (date && originalRentalDuration) {
                      // Auto-calculate pickup date: new delivery + original duration
                      const newPickupDate = date.add(originalRentalDuration, 'day');
                      console.log('Auto-calculating pickup date:', newPickupDate.format('YYYY-MM-DD'));
                      form.setFieldsValue({ pickup_date: newPickupDate });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item 
                name="pickup_date" 
                label={
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    Pickup Date
                  </span>
                }
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (isPickupDatePassed) return Promise.resolve();
                      
                      const deliveryDate = getFieldValue('delivery_date');
                      if (!value || !deliveryDate || isDeliveryDatePassed) {
                        return Promise.resolve();
                      }
                      if (value.isBefore(deliveryDate, 'day')) {
                        return Promise.reject(new Error('Pickup date cannot be before delivery date'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <DatePicker 
                  size="large" 
                  className="date-change-datepicker"
                  format="MM/DD/YYYY"
                  disabled={isSubmitting || isPickupDatePassed}
                  allowClear={!isPickupDatePassed}
                  showToday={true}
                  placeholder={isPickupDatePassed ? "Date has passed" : "Select pickup date"}
                  style={{ 
                    width: '100%',
                    borderRadius: '6px'
                  }}
                  disabledDate={disabledPickupDate}
                  onChange={(date) => {
                    // Validate rental duration when pickup date changes
                    const deliveryDate = form.getFieldValue('delivery_date');
                    if (date && deliveryDate) {
                      validateRentalDuration(deliveryDate, date);
                    }
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Subtle help text for rental duration policy */}
          <Row className="rental-info-container">
            <Col span={24}>
              <div className="rental-info-box">
                <div className="rental-info-text">
                  <strong>Note: </strong> Need a longer rental? Please use the &quot;Extend Rental&quot; option instead of changing dates here.
                </div>
              </div>
            </Col>
          </Row>

          {/* Display rental duration error if exists */}
          {rentalDurationError && (
            <Row style={{ marginBottom: '20px' }}>
              <Col span={24}>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fff2f0',
                  border: '1px solid #ffccc7',
                  borderRadius: '6px',
                  color: '#ff4d4f',
                  fontSize: '14px'
                }}>
                  {rentalDurationError}
                </div>
              </Col>
            </Row>
          )}

          <Row gutter={16} className='delivery-address'>
            <Col span={12}>
              <Form.Item 
                name="delivery_addr" 
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      Delivery Address
                    </span>
                    <Popover 
                      content={
                        <div className="box-popover">
                          <p>{bubbleContents[obj.title] || 'Loading...'}</p>
                        </div>
                      }
                      trigger="hover"
                      onVisibleChange={(visible) => {
                        if (visible) {
                          fetchBubbleContent(obj.title, setBubbleContents);
                        }
                      }}
                    >
                      <i className="fa fa-info-circle color-primary" aria-hidden="true"></i>
                    </Popover>
                  </div>
                }
                rules={[{ required: false, message: 'Please enter the delivery address' }]}
              >
                <GoogleAutoComplete
                  className={
                    (zipCodeError && !deliveryAddress) || (showValidationErrors && deliveryAddressManuallyChanged && !isDeliveryAddressValid)
                      ? 'form-control delivery-address-field-error'
                      : 'form-control'
                  }
                  apiKey={apiKey}
                  onPlaceSelected={(place) => {
                    if (!isDeliveryAddressChangeBlocked) {
                      setAddress(place);
                    }
                  }}
                  onChange={handleDeliveryAddressChange}
                  options={{
                    types: ['address'],
                    componentRestrictions: { country: 'us' }
                  }}
                  defaultValue={deliveryAddress}
                  disabled={isSubmitting || isDeliveryAddressChangeBlocked}
                  style={{
                    backgroundColor: isDeliveryAddressChangeBlocked ? '#f5f5f5' : '#fff',
                    borderRadius: '6px',
                    width: '100%',
                    borderColor: (showValidationErrors && deliveryAddressManuallyChanged && !isDeliveryAddressValid) ? '#ff4d4f' : undefined
                  }}
                />
                <p className="mb-0 ant-form-item-explain-error">
                  {zipCodeError ? zipCodeError : null}
                  {showValidationErrors && deliveryAddressManuallyChanged && !isDeliveryAddressValid ? 'Please select an address from the dropdown suggestions.' : null}
                </p>
                <p className="mb-0 text-success">
                  {!zipCodeError && deliveryAddress && !isDeliveryAddressChangeBlocked && isDeliveryAddressValid ? 'Services available in this area' : null}
                </p>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item 
                name="pickup_addr" 
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      Pickup Address
                    </span>
                    <Popover 
                      content={
                        <div className="box-popover">
                          <p>{bubbleContents[obj.title] || 'Loading...'}</p>
                        </div>
                      }
                      trigger="hover"
                      onVisibleChange={(visible) => {
                        if (visible) {
                          fetchBubbleContent(obj.title, setBubbleContents);
                        }
                      }}
                    >
                      <i className="fa fa-info-circle color-primary" aria-hidden="true"></i>
                    </Popover>
                  </div>
                }
                rules={[{ required: false, message: 'Please enter the pickup address' }]}
              >
                <GoogleAutoComplete
                  className={
                    (pickupZipCodeError && !pickupAddress) || (showValidationErrors && pickupAddressManuallyChanged && !isPickupAddressValid)
                      ? 'form-control delivery-address-field-error'
                      : 'form-control'
                  }
                  apiKey={apiKey}
                  onPlaceSelected={(place) => {
                    if (!isPickupAddressChangeBlocked) {
                      setPickupAddressFunc(place);
                    }
                  }}
                  onChange={handlePickupAddressChange}
                  options={{
                    types: ['address'],
                    componentRestrictions: { country: 'us' }
                  }}
                  defaultValue={pickupAddress}
                  disabled={isSubmitting || isPickupAddressChangeBlocked}
                  style={{
                    backgroundColor: isPickupAddressChangeBlocked ? '#f5f5f5' : '#fff',
                    borderRadius: '6px',
                    width: '100%',
                    borderColor: (showValidationErrors && pickupAddressManuallyChanged && !isPickupAddressValid) ? '#ff4d4f' : undefined
                  }}
                />
                <p className="mb-0 ant-form-item-explain-error">
                  {pickupZipCodeError ? pickupZipCodeError : null}
                  {showValidationErrors && pickupAddressManuallyChanged && !isPickupAddressValid ? 'Please select an address from the dropdown suggestions.' : null}
                </p>
                <p className="mb-0 text-success">
                  {!pickupZipCodeError && pickupAddress && !isPickupAddressChangeBlocked && isPickupAddressValid ? 'Services available in this area' : null}
                </p>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="delivery_window_1"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      Delivery Window 1
                    </span>
                  </div>
                }
                rules={[{ required: false, message: 'Please select the delivery window' }]}
              >
                <Select
                  size="large"
                  placeholder="Select delivery time window"
                  disabled={isSubmitting || isTimeSlotChangeBlocked}
                  onChange={() => forceUpdate({})}
                  style={{
                    borderRadius: '6px',
                    backgroundColor: isTimeSlotChangeBlocked ? '#f5f5f5' : '#fff'
                  }}
                >
                  {deliveryTimeSlots.map((slot) => (
                    <Select.Option key={slot.id} value={`${slot.start_time} - ${slot.end_time}`}>
                      {`${slot.start_time} - ${slot.end_time}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="pickup_window_1"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      Pickup Window 1
                    </span>
                  </div>
                }
                rules={[{ required: false, message: 'Please select the pickup window' }]}
              >
                <Select
                  size="large"
                  placeholder="Select pickup time window"
                  disabled={isSubmitting || isTimeSlotChangeBlocked}
                  onChange={() => forceUpdate({})}
                  style={{
                    borderRadius: '6px',
                    backgroundColor: isTimeSlotChangeBlocked ? '#f5f5f5' : '#fff'
                  }}
                >
                  {pickupTimeSlots.map((slot) => (
                    <Select.Option key={slot.id} value={`${slot.start_time} - ${slot.end_time}`}>
                      {`${slot.start_time} - ${slot.end_time}`}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="delivery_window_2"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      Delivery Window 2
                    </span>
                  </div>
                }
                rules={[{ required: false, message: 'Please select the delivery window' }]}
              >
                <Select
                  size="large"
                  placeholder="Select delivery time window"
                  disabled={isSubmitting || isTimeSlotChangeBlocked}
                  style={{
                    borderRadius: '6px',
                    backgroundColor: isTimeSlotChangeBlocked ? '#f5f5f5' : '#fff'
                  }}
                >
                  {deliveryTimeSlots
                    .filter((slot) => {
                      const window1Value = form.getFieldValue('delivery_window_1');
                      return !window1Value || window1Value !== `${slot.start_time} - ${slot.end_time}`;
                    })
                    .map((slot) => (
                      <Select.Option key={slot.id} value={`${slot.start_time} - ${slot.end_time}`}>
                        {`${slot.start_time} - ${slot.end_time}`}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="pickup_window_2"
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500' }}>
                      Pickup Window 2
                    </span>
                  </div>
                }
                rules={[{ required: false, message: 'Please select the pickup window' }]}
              >
                <Select
                  size="large"
                  placeholder="Select pickup time window"
                  disabled={isSubmitting || isTimeSlotChangeBlocked}
                  style={{
                    borderRadius: '6px',
                    backgroundColor: isTimeSlotChangeBlocked ? '#f5f5f5' : '#fff'
                  }}
                >
                  {pickupTimeSlots
                    .filter((slot) => {
                      const window1Value = form.getFieldValue('pickup_window_1');
                      return !window1Value || window1Value !== `${slot.start_time} - ${slot.end_time}`;
                    })
                    .map((slot) => (
                      <Select.Option key={slot.id} value={`${slot.start_time} - ${slot.end_time}`}>
                        {`${slot.start_time} - ${slot.end_time}`}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} className='delivery-address'>
            <Col span={12}>
              <Form.Item 
                name="delivery_apt_number" 
                label={<span style={{ fontSize: '14px', fontWeight: '500' }}>Delivery Apt. no</span>}
              >
                <Input 
                  size="large" 
                  placeholder={
                    (orderDetails?.delivery_adress?.apt_number || 
                     orderDetails?.delivery_details?.apt_number || 
                     orderDetails?.delivery_apt_no) 
                      ? "Apartment number (optional)"
                      : "-"
                  }
                  disabled={isSubmitting || isDeliveryAddressChangeBlocked}
                  style={{ 
                    borderRadius: '6px',
                    backgroundColor: isDeliveryAddressChangeBlocked ? '#f5f5f5' : '#fff'
                  }}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item 
                name="pickup_apt_number" 
                label={<span style={{ fontSize: '14px', fontWeight: '500' }}>Pickup Apt. no</span>}
              >
                <Input 
                  size="large" 
                  placeholder={
                    (orderDetails?.pickup_adress?.apt_number || 
                     orderDetails?.pickup_details?.apt_number || 
                     orderDetails?.pickup_apt_no) 
                      ? "Apartment number (optional)"
                      : "-"
                  }
                  disabled={isSubmitting || isPickupAddressChangeBlocked}
                  style={{ 
                    borderRadius: '6px',
                    backgroundColor: isPickupAddressChangeBlocked ? '#f5f5f5' : '#fff'
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

        </Form>
      </div>
      
      <div className="new-modal-footer">
        {/* Display 48-hour restriction message in footer */}
        {(isDeliveryAddressChangeBlocked || isPickupAddressChangeBlocked) && (
          <div style={{
            marginBottom: '18px',
            padding: '18px',
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#ff4d4f',
            lineHeight: '1.7'
          }}>
            <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
              Alert! Date & Address changes are not allowed within 48 hours of the scheduled date.
            </div>
            <div style={{ fontSize: '14px', marginBottom: '4px' }}>
              If you want to make any changes within 48 hours of your scheduled date, please contact our customer support for assistance.
            </div>
            <div style={{ fontSize: '14px', fontWeight: '500' }}>
              <strong>Email:</strong> info@bluebox.rent &nbsp;&nbsp;&nbsp;
              <strong>Phone:</strong> 650-502-9522
            </div>
          </div>
        )}
        
        <div className="date-change-submit-container" style={{ textAlign: 'right' }}>
          <Button 
            size="large" 
            type="primary" 
            className='pay-button' 
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || (isDeliveryDatePassed && isPickupDatePassed) || !!rentalDurationError}
            style={{
              backgroundColor: '#0000FF',
              borderColor: '#0000FF',
              height: '48px',
              fontSize: '15px',
              fontWeight: 'bold',
              borderRadius: '6px',
              minWidth: '150px'
            }}
          >
            {isSubmitting ? 'Processing...' : 'Save details'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

DateChangeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedOrder: PropTypes.object,
  onOrderUpdate: PropTypes.func 
};

export default DateChangeModal;